import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react'
import type { Company } from '@/hooks/useCompanies'

interface DeleteCompanyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  company: Company | null
  userCount: number
  onConfirm: () => Promise<void>
}

export function DeleteCompanyModal({
  open,
  onOpenChange,
  company,
  userCount,
  onConfirm,
}: DeleteCompanyModalProps) {
  const [confirmationText, setConfirmationText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const isConfirmed = confirmationText === company?.name

  const handleConfirm = async () => {
    if (!isConfirmed || !company) return

    setIsDeleting(true)
    try {
      await onConfirm()
      // Modal will be closed by parent component after successful deletion
      setConfirmationText('')
    } catch {
      setIsDeleting(false)
      // Error is handled by parent component
    }
  }

  const handleCancel = () => {
    setConfirmationText('')
    onOpenChange(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open || !isDeleting) {
      setConfirmationText('')
      onOpenChange(open)
    }
  }

  if (!company) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md bg-background">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-destructive" />
            Delete Company
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. Please confirm you want to delete this company.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Warning Message */}
          <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4">
            <p className="text-sm text-destructive font-medium mb-2">
              You are about to delete the company:
            </p>
            <p className="text-lg font-bold text-foreground">{company.name}</p>
          </div>

          {/* User Count Warning */}
          {userCount > 0 && (
            <div className="bg-muted border border-border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    {userCount} {userCount === 1 ? 'user' : 'users'} will be deleted
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    All users associated with this company will be permanently removed from the platform.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Type <span className="font-bold text-destructive">{company.name}</span> to confirm
            </label>
            <Input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Enter company name"
              className="w-full"
              disabled={isDeleting}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <motion.button
              type="button"
              onClick={handleCancel}
              disabled={isDeleting}
              className="flex-1 px-4 py-3 bg-secondary text-secondary-foreground rounded-lg font-semibold hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: isDeleting ? 1 : 1.02 }}
              whileTap={{ scale: isDeleting ? 1 : 0.98 }}
            >
              Cancel
            </motion.button>

            <motion.button
              type="button"
              onClick={handleConfirm}
              disabled={!isConfirmed || isDeleting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-destructive text-destructive-foreground rounded-lg font-semibold hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: !isDeleting && isConfirmed ? 1.02 : 1 }}
              whileTap={{ scale: !isDeleting && isConfirmed ? 0.98 : 1 }}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5" />
                  Delete Company
                </>
              )}
            </motion.button>
          </div>

          {/* Additional Warning */}
          {userCount > 0 && (
            <p className="text-xs text-center text-muted-foreground pt-2">
              This will permanently delete {userCount > 1 ? 'all users and their' : 'the user and their'} associated data.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
